import React, { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import api from '../services/api';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800', executing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800',
  rejected: 'bg-slate-100 text-slate-600',
};

function Actions() {
  const [tab, setTab] = useState('pending');
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setActions(await api.getActions(tab === 'pending' ? 'pending' : null));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (action, decision) => {
    setBusy(action.id);
    setError('');
    try {
      if (decision === 'confirm') await api.confirmAction(action.id);
      else if (decision === 'retry') await api.retryAction(action.id);
      else await api.rejectAction(action.id);
      await load();
    } catch (err) {
      setError(err.message);
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto max-w-5xl px-6 py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">AI Actions</h2>
            <p className="mt-2 text-slate-600">Review proposed changes and audit actions across your organization.</p>
          </div>
          <button onClick={load} className="self-start rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium">Refresh</button>
        </div>

        <div className="mt-6 flex gap-2 border-b border-slate-200">
          {['pending', 'history'].map(value => (
            <button key={value} onClick={() => setTab(value)}
              className={`border-b-2 px-4 py-3 text-sm font-semibold capitalize ${tab === value ? 'border-blue-700 text-blue-700' : 'border-transparent text-slate-500'}`}>
              {value}
            </button>
          ))}
        </div>

        {error && <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

        <div className="mt-6 space-y-4">
          {loading ? <p className="text-slate-500">Loading actions…</p> : actions.length === 0 ?
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              {tab === 'pending' ? 'There are no actions waiting for confirmation.' : 'No action history yet.'}
            </div> : actions.map(action => (
              <article key={action.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[action.status] || STATUS_STYLE.rejected}`}>{action.status}</span>
                      <code className="text-xs text-slate-500">{action.tool_name}</code>
                    </div>
                    <h3 className="mt-3 text-lg font-semibold text-slate-900">{action.summary}</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Requested by {action.origin?.connection_name || (action.origin?.source === 'ssdash_chat' ? 'SS Dash chat' : 'an authenticated VentES session')}
                      {' · '}{new Date(action.created_at).toLocaleString()}
                    </p>
                  </div>
                  {action.status === 'pending' && (
                    <div className="flex gap-2">
                      <button disabled={busy === action.id} onClick={() => decide(action, 'confirm')}
                        className="rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm</button>
                      <button disabled={busy === action.id} onClick={() => decide(action, 'reject')}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50">Reject</button>
                    </div>
                  )}
                  {action.status === 'failed' && (
                    <button disabled={busy === action.id} onClick={() => decide(action, 'retry')}
                      className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Prepare retry</button>
                  )}
                </div>
                <details className="mt-4 rounded-lg bg-slate-50 p-3">
                  <summary className="cursor-pointer text-sm font-medium text-slate-700">Request details</summary>
                  <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{JSON.stringify(action.arguments, null, 2)}</pre>
                </details>
                {action.result && (
                  <details className="mt-2 rounded-lg bg-green-50 p-3">
                    <summary className="cursor-pointer text-sm font-medium text-green-800">Result</summary>
                    <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-green-900">{JSON.stringify(action.result, null, 2)}</pre>
                  </details>
                )}
                {action.error && <div className="mt-3 rounded bg-red-50 p-3 text-sm text-red-700">{action.error}</div>}
              </article>
            ))}
        </div>
      </main>
    </div>
  );
}

export default Actions;
